import { defineStore } from "pinia";
import {
  computed,
  inject,
  reactive,
  Reactive,
  Ref,
  ref,
  toRaw,
  watch
} from "vue";
import { useRouter } from "vue-router";
import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { useTreeStore } from "./treeStore";
import { useMainStore } from "./mainStore";
import { axiosQueueElement, requestStatus } from "@/types/global";
import * as Sentry from "@sentry/vue";
import { useSocketStore } from "./socketStore";

export const useAxiosStore = defineStore("axiosStore", () => {
  const socketStore = useSocketStore();

  window.addEventListener("online", () => {
    console.log("online");
    online.value = true;
    for (const [id, data] of Object.entries(requests)) {
      if (data.status === "failed") {
        executeElementRequest(id);
      }
    }
  });

  window.addEventListener("offline", () => {
    console.log("offline");
    online.value = false;
  });

  const online = ref(true);

  const savingInBackground = ref(false);
  const requests: Reactive<Record<string, requestStatus>> = reactive({});

  const activeRequests = computed(() => {
    return Object.values(requests).filter(
      (request) => request.status !== "critical"
    ).length;
  });

  let timeOutSavingInBackground = null;
  watch(activeRequests, (newValue, oldValue) => {
    if (newValue === 0) {
      clearTimeout(timeOutSavingInBackground);
      savingInBackground.value = false;
    }
    if (newValue > 0 && oldValue === 0) {
      timeOutSavingInBackground = setTimeout(() => {
        savingInBackground.value = true;
      }, 1000);
    }
  });

  watch(savingInBackground, (newValue) => {
    if (newValue) {
      window.onbeforeunload = function () {
        console.log("User is leaving while saving");
        socketStore.socket.disconnect();
        return "Daten werden im Hintergrund gespeichert. Haben sie bitte einen Moment Geduld";
      };
    } else {
      window.onbeforeunload = function (e) {
        console.log("User is leaving the page (axiosStore)");
        socketStore.socket.disconnect();
      };
    }
  });

  function addElementRequest(
    config: AxiosRequestConfig,
    id: string,
    resolveCallback: Function = () => {},
    errorCallback: Function = () => {}
  ) {
    if (requests.hasOwnProperty(id)) {
      if (requests[id].next) {
        requests[id].next.config.data = Object.assign(
          {},
          requests[id].next.config.data,
          config.data
        );
      } else {
        requests[id].next = {
          config,
          resolveCallback,
          errorCallback
        };
      }
    } else {
      requests[id] = {
        current: {
          config,
          resolveCallback,
          errorCallback
        },
        count: 0,
        status: null
      };

      executeElementRequest(id);
    }
  }

  function executeElementRequest(id: string) {
    if (!requests[id]) {
      // No request with this id found
      return;
    }

    if (!online.value) {
      // No internet connection
      requests[id].status = "failed";
      return;
    }

    const { config, resolveCallback, errorCallback } = requests[id].current;

    requests[id].status = "pending";
    axios(config)
      .then((response) => {
        resolveCallback(response);
        if (requests[id].next) {
          requests[id].count = 0;
          requests[id].current = requests[id].next;
          requests[id].next = undefined;

          executeElementRequest(id);
        } else {
          delete requests[id];
        }
      })
      .catch((error) => {
        errorCallback(error);
        requests[id].count++;
        // 3 retries
        if (requests[id].count < 3) {
          executeElementRequest(id);
        } else {
          requests[id].status = "critical";
          console.error(
            "Critical error occurred: request failed 3 times",
            JSON.stringify(requests[id])
          );
          Sentry.captureException(error, { extra: { request: requests[id] } });

          // Capture failed request and ignore
          if (requests[id].next) {
            requests[id].count = 0;
            requests[id].current = requests[id].next;
            requests[id].next = undefined;

            executeElementRequest(id);
          } else {
            delete requests[id];
          }
        }
      });
  }

  function deleteRequest(
    config: AxiosRequestConfig,
    id: string,
    resolveCallback: Function = () => {},
    errorCallback: Function = () => {}
  ) {
    if (requests[id]?.status === "pending") {
      requests[id].next = { config, resolveCallback, errorCallback };
    } else {
      requests[id] = {
        status: null,
        current: { config, resolveCallback, errorCallback },
        count: 0,
        next: undefined
      };
      executeElementRequest(id);
    }
  }

  function execute(
    config: AxiosRequestConfig,
    resolveCallback: Function = () => {},
    errorCallback: Function = () => {}
  ) {
    // For executing independent requests
    axios(config)
      .then((response: AxiosResponse) => {
        resolveCallback(response);
      })
      .catch((error: any) => {
        console.error(error);
        Sentry.captureException(error);
        errorCallback(error);
      });
  }

  return {
    addElementRequest,
    deleteRequest,
    execute,
    online,
    requests,
    savingInBackground
  };
});
